import React from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import styled from 'styled-components'
import { defs } from 'components/Shared/defs'
import { device } from 'components/Shared/device'
import { FieldglassCompanyGroup } from 'api/admin/fieldglass/listFieldglassCompanyGroups'
import {
  FIELDGLASS_ISSUE_TYPE_OPTIONS,
  FieldglassOperationIssueType,
} from '../constants/issueTypes'

export type FieldglassShiftIssuesFilters = {
  companyGroupIds: number[] | null
  issueTypes: FieldglassOperationIssueType[] | null
  daysAgo: number
}

type NumberOption = { label: string; value: number }
type IssueTypeOption = { label: string; value: FieldglassOperationIssueType }

const DAYS_AGO_OPTIONS: NumberOption[] = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
]

const findSelectedNumberOptions = (
  options: NumberOption[],
  values: number[] | null
) => (values ? options.filter((o) => values.includes(o.value)) : [])

const findSelectedIssueTypeOptions = (
  values: FieldglassOperationIssueType[] | null
) =>
  values
    ? FIELDGLASS_ISSUE_TYPE_OPTIONS.filter((o) => values.includes(o.value))
    : []

const toNumberValues = (selected: MultiValue<NumberOption>) =>
  selected.length ? selected.map((o) => o.value) : null

const toIssueTypeValues = (selected: MultiValue<IssueTypeOption>) =>
  selected.length ? selected.map((o) => o.value) : null

type FieldglassIssuesFilterBarProps = {
  companyGroups: FieldglassCompanyGroup[]
  filters: FieldglassShiftIssuesFilters
  setFilters: (update: Partial<FieldglassShiftIssuesFilters>) => void
}

export const FieldglassIssuesFilterBar = ({
  companyGroups,
  filters,
  setFilters,
}: FieldglassIssuesFilterBarProps) => {
  const companyGroupOptions: NumberOption[] = companyGroups.map((group) => ({
    label: `${group.companyGroupName} (${group.companyGroupId})`,
    value: group.companyGroupId,
  }))

  const selectedDaysAgo =
    DAYS_AGO_OPTIONS.find((o) => o.value === filters.daysAgo) ?? null

  return (
    <FilterBarWrapper showBorderTop showBorderBottom>
      <SelectGroup>
        <Select
          isMulti
          isClearable
          isSearchable
          options={companyGroupOptions}
          styles={customStyles}
          name="companyGroupIds"
          placeholder="Company Group"
          value={findSelectedNumberOptions(
            companyGroupOptions,
            filters.companyGroupIds
          )}
          onChange={(selected) =>
            setFilters({ companyGroupIds: toNumberValues(selected) })
          }
          closeMenuOnSelect={false}
        />
        <Select
          isMulti
          isClearable
          isSearchable
          options={FIELDGLASS_ISSUE_TYPE_OPTIONS}
          styles={issueSelectStyles}
          name="issueTypes"
          placeholder="Issue"
          value={findSelectedIssueTypeOptions(filters.issueTypes)}
          onChange={(selected: MultiValue<IssueTypeOption>) =>
            setFilters({ issueTypes: toIssueTypeValues(selected) })
          }
          closeMenuOnSelect={false}
        />
        <Select
          isSearchable={false}
          options={DAYS_AGO_OPTIONS}
          styles={customStyles}
          name="daysAgo"
          placeholder="Time range"
          value={selectedDaysAgo}
          onChange={(selected: SingleValue<NumberOption>) => {
            if (selected) {
              setFilters({ daysAgo: selected.value })
            }
          }}
        />
      </SelectGroup>
    </FilterBarWrapper>
  )
}

const customStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 220,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const issueSelectStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 260,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const FilterBarWrapper = styled.div<{
  showBorderTop?: boolean
  showBorderBottom?: boolean
}>`
  border-bottom: ${(props) => (props.showBorderBottom ? '1px solid' : null)};
  border-top: ${(props) => (props.showBorderTop ? '1px solid' : null)};
  border-color: ${defs.veryLightBlack};
  padding: ${defs.paddingS};
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fafafa;
  @media ${device.smallerThanMobileL} {
    font-size: ${defs.fontS};
    height: 100%;
    padding-left: ${defs.paddingM};
  }
`

const SelectGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
`
